import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { replace } from "react-router-redux";

import { useMediaQuery } from "@ui-kit/components/index.es";

import { saveDraft } from "middleware/form";
import * as transferMiddleware from "middleware/transfer";
import { actions as formActions } from "reducers/form";
import { actions as transactionLinesActions } from "reducers/form/transactionLines";
import { actions as frequentDestinationActions } from "reducers/frequentDestination";
import { actions as holidaysActions } from "reducers/holidays";
import { actions as templateActions } from "reducers/template";
import { actions as transferActions } from "reducers/transfer";

import { getBoolean } from "util/config";
import * as configUtils from "util/config";
import * as dateUtils from "util/date";
import * as i18n from "util/i18n";
import { numberFormat } from "util/number";
import * as schedulerUtils from "util/scheduler";
import { saveLocation } from "util/transaction";
import { getTransferExecutePath } from "util/v3UxConfig";
import { isUxTransfersImprovementsEnabled } from "util/v3ShellLayoutSync";

import {
    applyTransferTadHint,
    isTransferExecuteEntryPath,
    isValidAlias,
    isValidCbu,
    matchTransferExecutePath,
    parseTransferAmount,
    TRANSFER_THIRD_PARTIES_FORM_ID,
} from "v3/pages/transfers/logic/transferExecuteShared";
import { buildTransferThirdPartiesValidationSchema } from "v3/pages/transfers/logic/transferExecuteValidation";

const ID_FORM = TRANSFER_THIRD_PARTIES_FORM_ID;
const ID_ACTIVITY = "transfers.thirdParties.send";

const INITIAL_VALUES = {
    debitAccount: "",
    transferKind: "",
    concept: "",
    creditAccount: "",
    amount: "",
    reference: "",
    sendEmail: false,
    email: "",
    emailText: "",
    scheduler: {},
};

/**
 * Business logic extracted from TransferThirdPartiesForm.jsx for legacy bridge and V3 ui-kit.
 *
 * @param {object} props
 * @returns {object}
 */
export default function useTransferThirdPartiesExecuteLogic(props) {
    const {
        creditAccountConfirmed,
        dispatch,
        isDesktop,
        fetching,
        fetchingDraft,
        fetchingSubmit,
        location,
        accounts,
        summary,
        templates,
        isTemplatesVisible,
        setIsTemplatesVisible,
        setAgendaVisible,
        limit,
        limitUsed,
        limits,
        onCreateTemplate,
        agendaVisible,
        listFrequentDestinations,
        lang,
        idEnvironment,
        selectedFromAgenda,
        isValidCbu: isValidCbuProp,
        hideAmount,
        idTransaction,
        concepts,
        isCVU,
        isRetailEnvironment,
        activeEnvironment,
        cvuCbuFromAlias,
        getLabel = i18n.get,
    } = props;

    const isMobile = useMediaQuery("min", "md");
    const extendedAmountVariableEnabled = getBoolean("increase.size.amount.link.iso8583", false);
    const enableHideAmounts = getBoolean("frontend.hide.accounts.balance.funcionalities", false);
    const referenceMaxLength = configUtils.getInteger("frontend.transfers.reference.maxLength", 12);
    const tadWarningText = configUtils.get("frontend.TAD.warning.text");

    const [values, setValues] = useState({ ...INITIAL_VALUES });
    const [draftTemplateButtonDisabled, setDraftTemplateButtonDisabled] = useState(true);
    const [flagAgendaDrawer, setFlagAgendaDrawer] = useState(false);
    const [formikProps, setFormikProps] = useState({});
    const [disabledMail, setDisabledMail] = useState(false);
    const [showFullList, setShowFullList] = useState(false);
    const [tadWarningPopupVisible, setTadWarningPopupVisible] = useState(false);
    const [tadWarningShowed, setTadWarningShowed] = useState(false);
    const tadWarningAcknowledgedRef = useRef(false);
    const [isScheduleModalVisible, setIsScheduleModalVisible] = useState(false);
    const [isTemplateOn, setIsTemplateOn] = useState(false);

    const {
        debitAccount,
        creditAccount,
        amount,
        reference,
        email,
        emailText,
        sendEmail,
        transferKind,
        concept,
    } = values;

    const isThirdPartiesModifyPath = matchTransferExecutePath(location?.pathname, "thirdParties", "modify");

    const getActivityIds = useCallback(() => ["transfers.thirdParties.send", "transfersCVU.thirdParties.send"], []);

    const getActivityId = useCallback(() => {
        const isEnableCVUCoelsaTransfer = configUtils.getBoolean(
            "frontend.show.transfers.services.Coelsa.CVU.functionalities",
        );
        if (isEnableCVUCoelsaTransfer && isCVU) {
            return "transfersCVU.thirdParties.send";
        }
        return "transfers.thirdParties.send";
    }, [isCVU]);

    const getActivityIdTad = useCallback(() => "transfers.thirdParties.tad.send", []);

    const transferKindOptions = useMemo(
        () => [
            {
                value: "same",
                label: getLabel("transfers.form.transfer.kind.same"),
            },
            {
                value: "other",
                label: getLabel("transfers.form.transfer.kind.other"),
            },
        ],
        [getLabel],
    );

    const conceptsFiltered = useMemo(() => {
        const excludedCvuConcepts = ["BRH", "SON", "ROP", "OIH", "BNR", "APC", "SIS"];
        const source = isCVU ? concepts.filter(({ id }) => !excludedCvuConcepts.includes(id)) : concepts;
        return source.map(({ id, name }) => ({
            value: id,
            label: `${id} - ${name}`,
        }));
    }, [concepts, isCVU]);

    const checkDisabledMail = useCallback((flagSendEmail, emailAdress) => {
        if (flagSendEmail && emailAdress === "") {
            setDisabledMail(true);
        } else if (emailAdress !== "" && flagSendEmail) {
            setDisabledMail(false);
        } else if (emailAdress === "" && !flagSendEmail) {
            setDisabledMail(false);
        }
    }, []);

    const selectedAccount = useCallback(() => accounts.find((account) => account.idProduct === values.debitAccount), [
        accounts,
        values.debitAccount,
    ]);

    const getDefaultAccount = useCallback(
        () => accounts?.find((account) => account.favorite)?.idProduct || accounts[0]?.idProduct,
        [accounts],
    );

    const selectedCurrency = useCallback(() => {
        const account = accounts.find((accountItem) => accountItem.idProduct === values.debitAccount);
        return getLabel(`currency.label.${account?.currency}`);
    }, [accounts, getLabel, values.debitAccount]);

    const mapAccounts = useMemo(
        () =>
            accounts.map((account) => ({
                accountName: account.productAlias || account.shortLabel,
                amount: account.balance,
                currency: getLabel(`currency.label.${account.currency}`),
                description: getLabel(`productType.${account.productType}`),
                id: account.idProduct,
                codeBank: account.cbu?.substring(0, 3) || "319",
            })),
        [accounts, getLabel],
    );

    const isTad = useCallback(
        (amountWithCurrency) => {
            const value = amountWithCurrency.amount;
            const environmentTypeTag = isRetailEnvironment ? "Retail" : "Corporate";
            const currencyTag = amountWithCurrency.currency.toString() === "0" ? "Pesos" : "Dolares";
            const minAmount = configUtils.getInteger(
                `frontend.TAD.minAmount${currencyTag}.${environmentTypeTag}`,
                100000,
            );
            const maxAmount = configUtils.getInteger(
                `frontend.TAD.maxAmount${currencyTag}.${environmentTypeTag}`,
                1000000,
            );

            if (isCVU) {
                return false;
            }

            if (activeEnvironment?.permissions?.transferDiferredAccreditation) {
                if (value >= minAmount && value <= maxAmount) {
                    return true;
                }
            }
            return false;
        },
        [activeEnvironment, isCVU, isRetailEnvironment],
    );

    const setHint = useCallback(
        (value) => {
            applyTransferTadHint(isTad(value), {
                setPopupVisible: setTadWarningPopupVisible,
                setShowed: setTadWarningShowed,
                isAcknowledged: () => tadWarningAcknowledgedRef.current,
                resetAcknowledged: () => {
                    tadWarningAcknowledgedRef.current = false;
                },
                onActive: () => {
                    setAgendaVisible(false);
                },
                onInactive: () => {
                    setAgendaVisible(true);
                },
            });
        },
        [isTad, setAgendaVisible],
    );

    const resolveIsValidCbu = useCallback(
        (value) => (typeof isValidCbuProp === "function" ? isValidCbuProp(value) : isValidCbu(value)),
        [isValidCbuProp],
    );

    const verifyCVU = useCallback(
        (value) => {
            if (resolveIsValidCbu(value)) {
                const isCVUvalue = value.startsWith("000");
                dispatch(transferActions.setIsCVU(isCVUvalue));
            } else if (isValidAlias(value)) {
                dispatch(transferActions.getCbuCvuFromAlias(value));
            }
        },
        [dispatch, resolveIsValidCbu],
    );

    const hasToAddBeneficiary = useCallback(
        (amountParameter, currencyParameter) => {
            const environmentTypeTag = isRetailEnvironment ? "retail" : "corporate";
            const currencyTypeTag = currencyParameter === 2 || currencyParameter === "2" ? "Dolares" : "Pesos";
            const amountControl = configUtils.getInteger(
                `frontend.controlAgenda.amount${currencyTypeTag}.${environmentTypeTag}`,
                10000,
            );
            return amountParameter >= amountControl;
        },
        [isRetailEnvironment],
    );

    const controlContactAgenda = useCallback(
        (value, selectedCreditAccount) => {
            const controlNewRecipientsAvailable = configUtils.getBoolean(
                "frontend.show.newRecipients.functionalities",
                false,
            );
            if (controlNewRecipientsAvailable) {
                return;
            }
            const controlAgendaAvailable = configUtils.getBoolean(
                "frontend.ControlAgenda.validation.transfers.enable",
                false,
            );

            const creditAccountUsed = selectedCreditAccount || values?.cbuOrAlias;
            const found = listFrequentDestinations.find(
                (frequentDestination) =>
                    frequentDestination.cbu === creditAccountUsed || frequentDestination.cbu === cvuCbuFromAlias,
            );
            let existCbu = false;
            if (found) {
                existCbu = true;
            } else {
                dispatch(transferActions.setSelectedFromAgenda(existCbu));
                dispatch(
                    transferActions.setAgendaDrawerValues({
                        cbuOrAliasAgenda: creditAccountUsed,
                        emailAgenda: "",
                        referenceAgenda: "",
                        tokenAgenda: "",
                    }),
                );
            }

            dispatch(transferActions.setFrequentDestinationExistOnList(existCbu));

            const hasCbuOrAliasOnField =
                creditAccountUsed !== "" && creditAccountUsed !== null && creditAccountUsed !== undefined;

            const hasToActivateFlagAgendaDrawer =
                controlAgendaAvailable &&
                hasToAddBeneficiary(parseTransferAmount(value?.amount), value?.currency) &&
                !selectedFromAgenda &&
                hasCbuOrAliasOnField &&
                !existCbu;

            dispatch(transferActions.setAgendaDrawerVisible(hasToActivateFlagAgendaDrawer));
            if (hasCbuOrAliasOnField && hasToActivateFlagAgendaDrawer) {
                dispatch(
                    transferActions.setAgendaDrawerValues({
                        cbuOrAliasAgenda: creditAccountUsed,
                        emailAgenda: "",
                        referenceAgenda: "",
                        tokenAgenda: "",
                    }),
                );
            }
            setFlagAgendaDrawer(hasToActivateFlagAgendaDrawer);
        },
        [
            cvuCbuFromAlias,
            dispatch,
            hasToAddBeneficiary,
            listFrequentDestinations,
            selectedFromAgenda,
            values?.cbuOrAlias,
        ],
    );

    const handleDebitAccountSelect = useCallback(
        (accountId) => {
            setValues((prev) => ({ ...prev, debitAccount: accountId }));
            dispatch(transferActions.accountLimitsListActivities(accountId, getActivityIds()));
        },
        [dispatch, getActivityIds],
    );

    const handleChangeAmount = useCallback(
        (a) => {
            const account = accounts.find((accountItem) => accountItem.idProduct === values.debitAccount);
            if (creditAccountConfirmed && account) {
                controlContactAgenda({ amount: parseTransferAmount(a), currency: account.currency });
            }
            setValues((prev) => ({ ...prev, amount: a }));
        },
        [accounts, controlContactAgenda, creditAccountConfirmed, values.debitAccount],
    );

    const handleSendEmailToggle = useCallback(() => {
        setValues((prev) => ({ ...prev, sendEmail: !prev.sendEmail }));
    }, []);

    const handleReferenceChange = useCallback((referenceValue) => {
        setValues((prev) => ({ ...prev, reference: referenceValue }));
    }, []);

    const handleEmailChange = useCallback(
        (emailValue) => {
            setValues((prev) => ({ ...prev, email: emailValue }));
            checkDisabledMail(sendEmail, emailValue);
        },
        [checkDisabledMail, sendEmail],
    );

    const handleEmailTextChange = useCallback((emailTextValue) => {
        setValues((prev) => ({ ...prev, emailText: emailTextValue }));
    }, []);

    const handleTransferKindChange = useCallback((option) => {
        setValues((prev) => ({ ...prev, transferKind: option }));
    }, []);

    const handleConceptChange = useCallback((option) => {
        setValues((prev) => ({ ...prev, concept: option }));
    }, []);

    const handleCreditAccountInputChange = useCallback((value) => {
        setValues((prev) => ({ ...prev, creditAccount: value, cbuOrAlias: value }));
    }, []);

    const handleCreditAccountClear = useCallback(() => {
        dispatch(transferActions.setCreditAccountConfirmed(false));
        setShowFullList(false);
        setValues((prev) => ({ ...prev, creditAccount: "", cbuOrAlias: "" }));
    }, [dispatch]);

    const handleAccountSearch = useCallback(
        async (cbuCvuOrAlias) => {
            let cbuCvu;
            let alias;
            if (resolveIsValidCbu(cbuCvuOrAlias)) {
                cbuCvu = cbuCvuOrAlias;
            } else {
                alias = cbuCvuOrAlias;
            }
            return new Promise((resolve, reject) => {
                transferMiddleware
                    .getAccountByCbu(cbuCvu, alias)
                    .then((response) => {
                        const { clientUser, accountDetail, documentType } = response.data.data;
                        if (!clientUser && !accountDetail) {
                            reject(response.data.message);
                        }
                        resolve({
                            dataAccount: {
                                owner: clientUser.firstName,
                                document: clientUser.cuil,
                                documentType,
                                bankName: accountDetail.bankName,
                                cbu: accountDetail.cbu,
                            },
                        });
                    })
                    .catch(() => {
                        reject(getLabel("problems.finding.cbu"));
                    });
            });
        },
        [getLabel, resolveIsValidCbu],
    );

    const handleConfirmCreditAccount = useCallback(
        (value) => {
            dispatch(transferActions.setCreditAccountConfirmed(true));
            setIsTemplateOn(false);
            verifyCVU(value);
            setDraftTemplateButtonDisabled(false);
            setValues((prev) => ({
                ...prev,
                creditAccount: value,
                cbuOrAlias: value,
            }));
            if (values.amount) {
                const account = selectedAccount();
                if (account) {
                    controlContactAgenda({ amount: values.amount, currency: account.currency }, value);
                }
            }
            dispatch(
                transferActions.setAgendaDrawerValues({
                    cbuOrAliasAgenda: value,
                    emailAgenda: "",
                    referenceAgenda: "",
                    tokenAgenda: "",
                }),
            );
        },
        [controlContactAgenda, dispatch, selectedAccount, values.amount, verifyCVU],
    );

    const changeCbu = useCallback(
        (record) => {
            const transferKindDestination = record.isOwn === "1" ? "same" : "other";
            setValues((prev) => ({
                ...prev,
                cbuOrAlias: record.account,
                transferKind: transferKindDestination,
                email: record.email,
            }));
            verifyCVU(record.account);
        },
        [verifyCVU],
    );

    const closeTADModal = useCallback(() => {
        tadWarningAcknowledgedRef.current = true;
        setTadWarningPopupVisible(false);
    }, []);

    const openTADModal = useCallback(() => {
        setTadWarningPopupVisible(true);
    }, []);

    const handleCreateTemplate = useCallback(
        (propsFormik) => {
            const account = accounts.find((accountItem) => accountItem.idProduct === values.debitAccount);
            onCreateTemplate({ ...propsFormik.values, currency: account?.currency });
        },
        [accounts, onCreateTemplate, values.debitAccount],
    );

    const getDraftSummary = useCallback(
        (draftValues) => {
            const {
                debitAccount: draftDebitAccount,
                creditAccount: draftCreditAccount,
                amount: draftAmount,
                currency,
                reference: draftReference,
                sendEmail: draftSendEmail,
                email: draftEmail,
                emailText: draftEmailText,
                tadTrx,
                concept: draftConcept,
                transferKind: draftTransferKind,
                scheduler,
                flagHint,
            } = draftValues;
            const completeDebitAccount = accounts.find(({ idProduct }) => idProduct === draftDebitAccount);
            const schedulerToSend = scheduler && scheduler.selectedOption !== schedulerUtils.TODAY ? scheduler : null;
            const isCbu = () => /^\d+$/.test(draftCreditAccount);
            return {
                accounts,
                debitAccount: completeDebitAccount,
                cbu: isCbu() ? draftCreditAccount : null,
                alias: !isCbu() ? draftCreditAccount : null,
                currency,
                amount: draftAmount,
                reference: draftReference,
                sendEmail: draftSendEmail,
                email: draftEmail,
                emailText: draftEmailText,
                transferKind: draftTransferKind?.value,
                concept: draftConcept?.value,
                scheduler: schedulerToSend,
                idTransaction,
                tadTrx,
                flagHint,
            };
        },
        [accounts, idTransaction],
    );

    const onSaveDraft = useCallback(
        (draftValues) => {
            if (!isUxTransfersImprovementsEnabled()) {
                setDraftTemplateButtonDisabled(true);
            }
            const {
                transferKind: draftTransferKind,
                concept: draftConcept,
                debitAccount: draftDebitAccount,
                creditAccount: draftCreditAccount,
            } = draftValues;
            const completeDebitAccount = accounts.find(({ idProduct }) => idProduct === draftDebitAccount);
            const parsedAmount = parseTransferAmount(draftValues.amount) || 0;
            const tadTrx = isTad({
                currency: completeDebitAccount.currency,
                amount: parsedAmount,
            });
            const activityId = getActivityId();
            dispatch(
                formActions.saveDraft({
                    idForm: ID_FORM,
                    idActivityDraft: tadTrx ? getActivityIdTad() : activityId,
                    data: {
                        ...draftValues,
                        cbuOrAlias: draftCreditAccount,
                        amount: {
                            quantity: parsedAmount,
                            currency: completeDebitAccount.currency,
                        },
                        currency: completeDebitAccount.currency,
                        debitAccount: { value: draftDebitAccount },
                        creditAccount: { value: draftCreditAccount },
                        transferKind: draftTransferKind?.value,
                        concept: draftConcept?.value,
                    },
                    idTransaction: idTransaction || null,
                    scheduler: draftValues?.scheduler,
                    summary: getDraftSummary({
                        ...draftValues,
                        amount: parsedAmount,
                        currency: completeDebitAccount.currency,
                    }),
                }),
            );
        },
        [accounts, dispatch, getActivityId, getActivityIdTad, getDraftSummary, idTransaction, isTad],
    );

    const onSubmit = useCallback(
        (formValues) => {
            if (tadWarningPopupVisible) {
                return;
            }

            const {
                debitAccount: submitDebitAccount,
                creditAccount: submitCreditAccount,
                amount: submitAmount,
                reference: submitReference,
                sendEmail: submitSendEmail,
                email: submitEmail,
                emailText: submitEmailText,
                concept: submitConcept,
                transferKind: submitTransferKind,
                flagHint,
                flagAgendaDrawer: submitFlagAgendaDrawer,
            } = formValues;

            if (submitFlagAgendaDrawer) {
                dispatch(transferActions.setAgendaDrawerVisible(true));
                return;
            }

            const completeDebitAccount = accounts.find(({ idProduct }) => idProduct === submitDebitAccount);
            const currency = parseInt(completeDebitAccount.currency, 10);
            const isCbu = () => /^\d+$/.test(submitCreditAccount);

            let schedulerToSend =
                values.scheduler && values.scheduler.selectedOption !== schedulerUtils.TODAY
                    ? { ...values.scheduler }
                    : null;
            if (schedulerToSend) {
                schedulerToSend = { ...schedulerToSend, newValueDate: schedulerToSend.valueDate };
                delete schedulerToSend.valueDate;
            }

            const tadTrx = isTad({
                currency,
                amount: parseTransferAmount(submitAmount),
            });

            if (isDesktop) {
                dispatch(replace(getTransferExecutePath("thirdParties", { suffix: "back" })));
            }
            dispatch(
                transferActions.createExternalTransfer(
                    {
                        accounts,
                        debitAccount: completeDebitAccount,
                        cbu: isCbu() ? submitCreditAccount : null,
                        alias: !isCbu() ? submitCreditAccount : null,
                        currency,
                        amount: parseTransferAmount(submitAmount),
                        reference: submitReference,
                        sendEmail: submitSendEmail,
                        email: submitEmail,
                        emailText: submitEmailText,
                        concept: submitConcept.value,
                        transferKind: submitTransferKind.value,
                        scheduler: schedulerToSend,
                        idTransaction: summary.idTransaction,
                        tadTrx,
                        flagHint,
                        isCVU,
                    },
                    null,
                ),
            );

            if (isThirdPartiesModifyPath) {
                saveDraft();
            }
        },
        [
            accounts,
            dispatch,
            isCVU,
            isDesktop,
            isTad,
            isThirdPartiesModifyPath,
            summary.idTransaction,
            tadWarningPopupVisible,
            values.scheduler,
        ],
    );

    const handleTemplateSelect = useCallback(
        (template) => {
            const amountVal = template.amount?.amount || 0;
            const amountWithCurrency = template.amount;
            verifyCVU(template.cbuOrAlias);
            const isTadTrx = isTad(amountWithCurrency);
            const creditAccountTemplate = template.creditAccount || template.cbuOrAlias;
            setIsTemplatesVisible(false);
            setValues((prev) => ({
                ...prev,
                debitAccount: template.debitAccount,
                creditAccount: creditAccountTemplate,
                cbuOrAlias: creditAccountTemplate,
                transferKind: transferKindOptions.find(({ value }) => value === template.transferKind),
                concept: conceptsFiltered.find(({ value }) => value === template.concept),
                amount: amountVal,
                reference: template.reference,
                sendEmail: template.sendEmail,
                emailText: template.emailText,
                email: template.email,
                flagHint: isTadTrx,
                tadTrx: isTadTrx,
            }));
            setShowFullList(false);
            setDraftTemplateButtonDisabled(true);
            checkDisabledMail(template.sendEmail, template.email);
            dispatch(transferActions.setCreditAccountConfirmed(false));
            dispatch(transactionLinesActions.loadTransactionLinesTemplateData(template));
            if (template.debitAccount) {
                dispatch(transferActions.accountLimitsListActivities(template.debitAccount, getActivityIds()));
            }
            if (creditAccount === creditAccountTemplate) {
                setIsTemplateOn(true);
            }
            if (creditAccountTemplate && formikProps?.setFieldTouched) {
                formikProps.setFieldTouched("creditAccount", true);
            }
        },
        [
            checkDisabledMail,
            conceptsFiltered,
            creditAccount,
            dispatch,
            formikProps,
            getActivityIds,
            isTad,
            setIsTemplatesVisible,
            transferKindOptions,
            verifyCVU,
        ],
    );

    const handleSchedulerChange = useCallback((data) => {
        if (data.frequency === schedulerUtils.ONCE) {
            setValues((prev) => ({
                ...prev,
                scheduler: {
                    selectedOption: data.frequency,
                    valueDate: data.startsOn,
                    program: null,
                },
            }));
        } else {
            const newProgramData = {
                frequency: data.frequency === schedulerUtils.CUSTOM ? data.customFreq : data.frequency,
                frequencyValue: data.frequency === schedulerUtils.CUSTOM ? data.customFreqValue : "1",
                days: dateUtils.getDayFromDate(data.startsOn),
                day: data.startsOn.getDate(),
                occurrence: null,
                startsOn: data.startsOn,
                lapse: data.lapse.lapse,
                number: data.lapse.number,
                date: data.lapse.date,
                isCustom: data.frequency === schedulerUtils.CUSTOM,
                selectedOption: data.frequency,
            };
            if (newProgramData.frequency === schedulerUtils.WEEK && newProgramData.isCustom) {
                newProgramData.days = data.days;
            }
            if (newProgramData.frequency === schedulerUtils.MONTH && data.occurrenceType === "occurrence") {
                newProgramData.occurrence = dateUtils.weekOfMonth(moment(data.startsOn));
                newProgramData.day = null;
            }
            setValues((prev) => ({
                ...prev,
                scheduler: {
                    selectedOption: newProgramData.frequency,
                    program: newProgramData,
                },
            }));
        }
        setIsScheduleModalVisible(false);
    }, []);

    const openScheduleModal = useCallback(() => {
        setIsScheduleModalVisible(true);
    }, []);

    const closeScheduleModal = useCallback(() => {
        setIsScheduleModalVisible(false);
    }, []);

    const customHandleKeyDown = useCallback(
        (e) => {
            const { decimalSeparator } = numberFormat(lang);
            const { value, selectionStart, selectionEnd } = e.target;
            const integerPart = value.split(decimalSeparator)[0].replace(/\D/g, "");
            const extendedAmountEnabled = getBoolean("increase.size.amount.link.iso8583", false);
            const maxSize = extendedAmountEnabled ? 15 : 10;

            const allowedKeys = [
                "Backspace",
                "Delete",
                "ArrowLeft",
                "ArrowRight",
                "Tab",
                "Home",
                "End",
                "Control",
                "Meta",
                "Shift",
            ];

            if (e.ctrlKey || e.metaKey || e.shiftKey) {
                return;
            }

            const isAllSelected = selectionStart === 0 && selectionEnd === value.length;
            const decimalIndex = value.indexOf(decimalSeparator);
            if (
                (e.key === decimalSeparator || e.key === ".") &&
                (selectionStart <= decimalIndex || decimalIndex === -1) &&
                integerPart.length >= maxSize
            ) {
                return;
            }

            if (isAllSelected) {
                return;
            }

            if (
                integerPart.length >= maxSize &&
                (selectionStart <= decimalIndex || decimalIndex === -1) &&
                !allowedKeys.includes(e.key)
            ) {
                e.preventDefault();
            }
        },
        [lang],
    );

    const validationSchema = useMemo(() => buildTransferThirdPartiesValidationSchema(getLabel, sendEmail), [
        getLabel,
        sendEmail,
    ]);

    const isLoading = (fetching && !accounts.length) || !values.debitAccount || fetchingSubmit;

    const continueDisabled = !creditAccountConfirmed || disabledMail;

    useEffect(() => {
        dispatch(templateActions.loadTemplateList(ID_FORM));
        dispatch(frequentDestinationActions.loadListRequest(idEnvironment, null));

        return () => {
            dispatch(transferActions.resetAgendaDrawerValues());
        };
    }, [dispatch, idEnvironment]);

    useEffect(() => {
        const { debitAccount: debitAccountDraft } = values;
        dispatch(transferActions.preActivity());
        dispatch(holidaysActions.listHolidays());
        dispatch(transferActions.resetAgendaDrawerValues());
        const { cbu, email: summaryEmail, transferKind: summaryTransferKind, isFrequentDestinations } = summary;

        if (isTransferExecuteEntryPath(location?.pathname)) {
            saveLocation(props);
            if (location?.state) {
                const { isInternal, debitAccount: debAcc } = location?.state;
                dispatch(transferActions.changeTransferGroup(isInternal));
                setValues((prev) => ({
                    ...prev,
                    debitAccount: debAcc,
                    creditAccount: "",
                    amount: {
                        amount: 0,
                        currency: "",
                    },
                    cbuOrAlias: "",
                    reference: "",
                    email: "",
                    emailText: "",
                    concept: "VAR",
                    transferKind: "",
                }));
                dispatch(transferActions.accountLimitsListActivities(debAcc, getActivityIds()));
            } else {
                const isInternalTrx =
                    summaryTransferKind === "other" ? false : activeEnvironment.permissions.transferInternal;

                if (!isInternalTrx && isFrequentDestinations) {
                    if (cbu) {
                        setValues((prev) => ({
                            ...prev,
                            cbuOrAlias: cbu,
                            email: summaryEmail,
                            transferKind: summaryTransferKind,
                        }));
                    }
                } else if (summaryEmail && isFrequentDestinations) {
                    setValues((prev) => ({
                        ...prev,
                        email: summaryEmail,
                    }));
                }
                dispatch(transferActions.changeTransferGroup(isInternalTrx));
            }
        } else if (isThirdPartiesModifyPath && debitAccountDraft) {
            dispatch(transferActions.accountLimitsListActivities(debitAccountDraft, getActivityIds()));
        }
    }, []);

    useEffect(() => {
        const {
            transferKind: summaryTransferKind,
            creditAccount: summaryCreditAccount,
            reference: summaryReference,
            amount: summaryAmount,
            concept: summaryConcept,
            email: summaryEmail,
            emailText: summaryEmailText,
            sendEmail: summarySendEmail,
            cbu,
            alias,
            scheduler,
        } = summary;
        const creditAccountTemplate = summaryCreditAccount?.value || summaryCreditAccount?.idProduct || cbu || alias;
        setValues((prev) => ({
            ...prev,
            creditAccount: creditAccountTemplate,
            amount: summaryAmount || "",
            transferKind: transferKindOptions.find(({ value }) => value === summaryTransferKind),
            concept: conceptsFiltered[0] || conceptsFiltered.find(({ value }) => value === summaryConcept),
            reference: summaryReference || "",
            email: summaryEmail || "",
            emailText: summaryEmailText || "",
            sendEmail: summarySendEmail,
            scheduler,
        }));
        setShowFullList(false);
        setDraftTemplateButtonDisabled(true);
        checkDisabledMail(summarySendEmail, summaryEmail);
    }, [summary]);

    useEffect(() => {
        if (selectedFromAgenda) {
            setFlagAgendaDrawer(false);
        }
    }, [listFrequentDestinations, selectedFromAgenda]);

    useEffect(() => {
        const amountField = document.getElementById("amount");
        if (amountField) {
            amountField.focus();
        }
    }, [showFullList]);

    useEffect(() => {
        if (!selectedAccount()) {
            const defaultAccount = getDefaultAccount();
            if (defaultAccount) {
                setValues((prev) => ({ ...prev, debitAccount: defaultAccount }));
                dispatch(transferActions.accountLimitsListActivities(defaultAccount, getActivityIds()));
                if (formikProps?.setValues) {
                    formikProps.setValues({ debitAccount: defaultAccount });
                }
            }
        }
    }, [values.debitAccount]);

    useEffect(() => {
        if (creditAccountConfirmed) {
            dispatch(transferActions.setCreditAccountConfirmed(false));
        }
    }, [values.creditAccount]);

    useEffect(() => {
        if (fetchingSubmit || !amount || !debitAccount) {
            return;
        }
        const parsedAmount = parseTransferAmount(amount);
        const foundAccount = accounts?.find((account) => account.idProduct === debitAccount);
        if (foundAccount) {
            const { currency } = foundAccount;
            if (parsedAmount > 0) {
                setHint({ amount: parsedAmount, currency });
            }
        }
    }, [accounts, amount, debitAccount, fetchingSubmit, setHint]);

    useEffect(() => {
        checkDisabledMail(sendEmail, values.email);
    }, [checkDisabledMail, sendEmail, values.email]);

    useEffect(() => {
        if (!values.concept && conceptsFiltered[0]) {
            setValues((prev) => ({ ...prev, concept: conceptsFiltered[0] }));
        }
    }, [conceptsFiltered, values.concept]);

    useEffect(() => {
        if (!values.debitAccount) {
            const { debitAccount: summaryDebitAccount } = summary;
            let defaultAccount;
            if (summaryDebitAccount) {
                defaultAccount = summaryDebitAccount.value || summaryDebitAccount.idProduct || summaryDebitAccount;
            } else {
                defaultAccount = getDefaultAccount();
            }
            if (defaultAccount) {
                setValues((prev) => ({ ...prev, debitAccount: defaultAccount }));
                dispatch(transferActions.accountLimitsListActivities(defaultAccount, getActivityIds()));
                if (formikProps?.setValues) {
                    formikProps.setValues({ debitAccount: defaultAccount });
                }
            }
        }
    }, [values.debitAccount, summary]);

    useEffect(() => {
        if (showFullList && draftTemplateButtonDisabled) {
            setDraftTemplateButtonDisabled(false);
        }
    }, [draftTemplateButtonDisabled, showFullList]);

    useEffect(() => {
        if (values.debitAccount && values.transferKind && creditAccountConfirmed && !showFullList) {
            setShowFullList(true);
        }
    }, [creditAccountConfirmed, showFullList, values.debitAccount, values.transferKind]);

    useEffect(() => {
        if (formikProps?.setValues) {
            formikProps.setValues({
                debitAccount,
                transferKind,
                concept,
                creditAccount,
                amount,
                reference,
                sendEmail,
                email,
                emailText,
            });
        }
    }, [
        amount,
        concept,
        creditAccount,
        debitAccount,
        email,
        emailText,
        formikProps,
        reference,
        sendEmail,
        transferKind,
    ]);

    return {
        formId: ID_FORM,
        idActivity: ID_ACTIVITY,
        values,
        setValues,
        formikProps,
        setFormikProps,
        draftTemplateButtonDisabled,
        setDraftTemplateButtonDisabled,
        flagAgendaDrawer,
        disabledMail,
        showFullList,
        setShowFullList,
        tadWarningPopupVisible,
        setTadWarningPopupVisible,
        tadWarningShowed,
        isScheduleModalVisible,
        setIsScheduleModalVisible,
        isTemplateOn,
        setIsTemplateOn,
        isMobile,
        isThirdPartiesModifyPath,
        extendedAmountVariableEnabled,
        enableHideAmounts,
        referenceMaxLength,
        tadWarningText,
        mapAccounts,
        transferKindOptions,
        conceptsFiltered,
        selectedCurrency,
        selectedAccount,
        validationSchema,
        initialValues: INITIAL_VALUES,
        isLoading,
        fetching,
        fetchingDraft,
        fetchingSubmit,
        continueDisabled,
        draftDisabled: draftTemplateButtonDisabled || fetchingDraft,
        templateDisabled: draftTemplateButtonDisabled,
        agendaVisible,
        templates,
        isTemplatesVisible,
        setIsTemplatesVisible,
        limit,
        limitUsed,
        limits,
        hideAmount,
        creditAccountConfirmed,
        getActivityId,
        getActivityIds,
        getActivityIdTad,
        parseAmount: parseTransferAmount,
        isTad,
        setHint,
        checkDisabledMail,
        verifyCVU,
        resolveIsValidCbu,
        handleDebitAccountSelect,
        handleChangeAmount,
        handleSendEmailToggle,
        handleReferenceChange,
        handleEmailChange,
        handleEmailTextChange,
        handleTransferKindChange,
        handleConceptChange,
        handleCreditAccountInputChange,
        handleCreditAccountClear,
        handleAccountSearch,
        handleConfirmCreditAccount,
        handleCreateTemplate,
        handleTemplateSelect,
        onSaveDraft,
        onSubmit,
        handleSchedulerChange,
        openScheduleModal,
        closeScheduleModal,
        customHandleKeyDown,
        changeCbu,
        closeTADModal,
        openTADModal,
        controlContactAgenda,
        hasToAddBeneficiary,
        getDraftSummary,
    };
}
